import { Form, Formik, FormikConfig } from 'formik'
import compact from 'lodash/compact'
import noop from 'lodash/noop'
import React, { FC, useCallback, useContext } from 'react'
import { usePopupManager } from 'react-popup-manager'
import styled from 'styled-components'
import { object, SchemaOf, string } from 'yup'

import { ButtonWithProgress } from '@Components/Button/ButtonWithProgress'
import { ErrorModal } from '@Components/ErrorModal'
import { FeedbackModal } from '@Components/FeedbackModal'
import { TextField } from '@Components/FormElements/Formik/TextField'
import { SizeSelector } from '@Components/SizeSelector'
import { Product } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useMParticle } from '@Globals/MParticle/useMParticle'
import { useFilteredVariants } from '@Globals/Product/useFilteredVariants'
import { up } from '@Globals/Style/breakpoints'
import theme from '@Globals/Style/theme'
import { useTranslation } from '@Globals/Translations'

import { Container } from './styled'

interface NotifyMeData {
  email?: string
  phone?: string
  size?: string
}

const requiredStringErrorLabel = 'cms:INPUT_FIELD_PLEASE_CHOOSE_ONE_OR_BOTH'

const StyledSubmitButton = styled(ButtonWithProgress)({
  width: '100%',

  [up('mobileStyleThreshold')]: {
    width: 'auto',
  },
})

const StyledErrorMessage = styled.div({
  color: theme.colors.red,
  margin: `${theme.padding.phablet} 0`,
})

const NotifyMeSchema: SchemaOf<NotifyMeData> = object()
  .shape({
    email: string().email(),
    phone: string(),
    size: string(),
  })

interface SizeOutOfStockProp {
  className?: HTMLDivElement['className']
  onAbort?: () => void
  product: Product
  selectedSize?: string
  showSizeSelector?: boolean
}

export const SizeOutOfStock: FC<SizeOutOfStockProp> = ({
  className,
  onAbort = noop,
  product,
  selectedSize,
  showSizeSelector,
}) => {
  const { t } = useTranslation()
  const { storeURL } = useContext(MetadataContext)
  const { closeAll: closeAllModals, open: openModal } = usePopupManager()
  // Including "dead" variants doesn't really make much sense if you think about it, but the selector in this
  // module will (likely) only be shown when no variants on the product are available, which is a very narrow
  // use-case in itself. In that case, the user might hold out hope that it'll come back in stock at some point,
  // and who are we to deny their dreams? No-one, that's who.
  const { availableVariants } = useFilteredVariants({ includeVariantsThatWillNeverRestock: true, product })
  const { otherEvent } = useMParticle()

  // You may be wondering why I'm writing a custom validator instead of just relying on the schema.
  // Well, using the schema directly in an "either-or" situation, which this is, didn't work at all,
  // and rather than spend a billion hours getting Yup to cooperate, this seemed the better solution.
  const validator = useCallback(async (values: NotifyMeData) => {
    const validMail = values.email && await NotifyMeSchema.fields.email.isValid(values.email)
    const validPhone = values.phone && await NotifyMeSchema.fields.phone.isValid(values.phone)

    if (!validMail && !validPhone) {
      return {
        email: true,
        phone: true,
      }
    }

    return true
  }, [])

  const onSubmit: FormikConfig<NotifyMeData>['onSubmit'] = async (values: NotifyMeData, formikHelpers) => {
    let isSuccess = false
    try {
      const url = storeURL('thsystem/requests/outofstock')
      const productId = values.size || selectedSize || ''
      const formValues = { ...values, isNewMethod: 'true', productid: productId }
      await fetch(url, {
        body: new URLSearchParams(formValues).toString(),
        method: 'POST',
      })

      // Let's tell the parent component we have nothing more to do here.
      onAbort()

      openModal(
        FeedbackModal,
        {
          onHide: closeAllModals,
          text: t('cms:PP_OUT_OF_STOCK_OVERLAY_CONTACT_YOU_SOON'),
        },
      )
      isSuccess = true
    } catch (error) {
      onAbort()
      openModal(ErrorModal, { onHide: closeAllModals })
    } finally {
      otherEvent({
        eventName: 'product_out_of_stock_opt_in_res',
        attributes: {
          is_success: isSuccess,
          is_size_product: availableVariants.length !== 0,
          is_email_added: !!values.email,
          is_phone_number_added: !!values.phone,
        },
      })
    }

    // Reset the form, just because.
    formikHelpers.resetForm()
  }

  return (
    <Container className={className}>
      <p className="title">{t(availableVariants.length > 0 ? 'cms:SIZE_IS_OUT_OF_STOCK' : 'cms:OUT_OF_STOCK')}</p>
      <p>{t(availableVariants.length > 0 ? 'cms:WANT_TO_BE_NOTIFIED_WHEN_SIZE_IS_AVAILABLE_AGAIN' : 'products:PP_OUT_OF_STOCK_WANT_TO_BE_NOTIFIED')}</p>
      <p>{t('cms:PP_OUT_OF_STOCK_HOW_TO_BE_NOTIFIED')}</p>
      <Formik<NotifyMeData>
        initialValues={{ email: '', phone: '' }}
        onSubmit={onSubmit}
        validate={validator}
        validateOnBlur={false}
        validateOnChange
      >
        {({
          errors,
          handleBlur,
          handleChange,
          isSubmitting,
          values,
        }) => (
          <Form noValidate>
            {showSizeSelector && availableVariants.length > 0 && (
              <SizeSelector
                hideMySizeIsNotHere
                hideOutOfStockLabel
                hidePlaceholder
                name="size"
                onBlur={handleBlur}
                onChange={handleChange}
                sizes={availableVariants}
                value={values.size}
              />
            )}
            <TextField
              error={errors.email && values.email && t('cms:INPUT_FIELD_EMAIL_IS_INVALID')}
              id="th-size-out-of-stock-email"
              label={t('cms:INPUT_FIELD_YOUR_EMAIL')}
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              type="email"
              value={values.email}
            />
            <TextField
              error={undefined}
              id="th-size-out-of-stock-phone"
              label={t('cms:INPUT_FIELD_YOUR_PHONE_NUMBER')}
              name="phone"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.phone}
            />

            {
              // Only show the "choose one or both options" message if any errors exist (meaning the validator has run), and...
              compact(Object.values(errors)).length > 0
              // ... if no values exist in the current data at all.
              && (![values.email, values.phone].join('').length)
              && (
                <StyledErrorMessage>{t(requiredStringErrorLabel)}</StyledErrorMessage>
              )
            }

            <div className="buttonWrap">
              <StyledSubmitButton
                disabled={isSubmitting}
                loading={isSubmitting}
                type="submit"
              >
                {t('cms:INPUT_FIELD_SEND_EMAIL_OR_SMS_BUTTON_TEXT')}
              </StyledSubmitButton>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  )
}
